import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "monthlyCanvas",
    "monthlyCountsCanvas",
    "monthlyAverageWrap",
    "monthlyCountsWrap",
    "monthlySectionTitle",
    "monthlyAverageToggle",
    "monthlyCountsToggle",
    "cohortCanvas",
    "overlay",
    "initial",
    "cohortTableBody",
    "factorOptions",
    "varianceVizCanvas",
    "figureCardTemplate",
    "primaryAxisSelect",
    "secondaryAxis",
    "propertyRollupRow",
    "propertyRollupToggle",
    "propertyRollupHidden",
    "primaryPills",
    "primaryHiddenInputs",
    "secondaryPillsSection",
    "secondaryPills",
    "secondaryHiddenInputs",
    "reissueReadout"
  ]
  static values = { url: String, internalProperties: Array }

  connect() {
    console.log('[ReleaseVariance] connect')
    this.monthlyCtx = this.monthlyCanvasTarget.getContext('2d')
    this.cohortCtx = this.cohortCanvasTarget.getContext('2d')
    this.varianceVizCtx = this.varianceVizCanvasTarget.getContext('2d')
    this._monthlyPointer = null
    this._monthlyFanDir = null

    // Fixed-position portal so cards overlay the whole viewport (and edge detection uses window bounds).
    this._monthlyCardsPortal = document.getElementById('monthly-cards-tooltip-portal')
    if (!this._monthlyCardsPortal) {
      const el = document.createElement('div')
      el.id = 'monthly-cards-tooltip-portal'
      el.style.position = 'fixed'
      el.style.left = '0'
      el.style.top = '0'
      el.style.width = '100vw'
      el.style.height = '100vh'
      el.style.pointerEvents = 'none'
      el.style.zIndex = '2147483647'
      el.style.display = 'none'
      document.body.appendChild(el)
      this._monthlyCardsPortal = el
    }

    let initial = {}
    try {
      if (this.hasInitialTarget) {
        initial = JSON.parse(this.initialTarget.textContent || '{}')
      }
    } catch (e) {
      console.error('[ReleaseVariance] failed to parse initial JSON', e)
      initial = {}
    }
    this.monthlyData = Array.isArray(initial.monthly) ? initial.monthly : []
    this.cohortData = Array.isArray(initial.cohorts) ? initial.cohorts : []
    this.varianceVizData = initial.variance_viz || null
    this.axisValueCounts = this.varianceVizData?.axis_value_counts || {}
    this.selectedPrimaryValues = new Set()
    this.selectedSecondaryValues = new Set()

    this.factorOptionsData = {}
    try {
      if (this.hasFactorOptionsTarget) {
        this.factorOptionsData = JSON.parse(this.factorOptionsTarget.textContent || '{}') || {}
      }
    } catch (e) {
      console.warn('[ReleaseVariance] failed to parse factor options JSON', e)
      this.factorOptionsData = {}
    }

    console.log('[ReleaseVariance] targets:', {
      hasForm: this.hasFormTarget,
      hasMonthlyCanvas: this.hasMonthlyCanvasTarget,
      hasCohortCanvas: this.hasCohortCanvasTarget,
      hasOverlay: this.hasOverlayTarget,
      url: this.urlValue,
      monthlyCount: this.monthlyData.length,
      cohortCount: this.cohortData.length
    })

    this.buildCharts()
    this.showMonthlyAverage()
    this.renderCohortTable()
    this.applyVarianceVizStateFromUrl()
    this.syncPrimaryAxisUIFromDOM()
    this.syncSecondaryOptions()
    this.syncPropertyRollupVisibility()
    // Render pills based on URL selections (if present); otherwise default to all enabled.
    this.syncPrimaryPills(this.selectedPrimaryValues.size === 0)
    this.syncSecondaryPills(this.selectedSecondaryAxisHasExplicitSelection() ? false : (this.selectedSecondaryValues.size === 0))
    this.renderVarianceViz()

    this.debouncedFetch = this.debounce(() => this.fetchData(), 200)
    this.onChange = (e) => {
      this.debouncedFetch()
    }
    this.formTarget.addEventListener('change', this.onChange)
    this.formTarget.addEventListener('submit', (e) => { e.preventDefault(); this.fetchData() })
    console.log('[ReleaseVariance] event listeners attached')
  }

  selectedSecondaryAxisHasExplicitSelection() {
    // If the URL explicitly specified secondary_values_present=1, we should respect it even if empty.
    try {
      const q = new URLSearchParams(window.location.search || '')
      return q.get('secondary_values_present') === '1'
    } catch (_) {
      return false
    }
  }

  applyVarianceVizStateFromUrl() {
    // Ensure pills/selects reflect persisted URL query params on refresh.
    let q
    try {
      q = new URLSearchParams(window.location.search || '')
    } catch (_) {
      return
    }

    const primary = q.get('primary_group_by')
    const secondary = q.get('secondary_group_by')
    const rollup = q.get('property_rollup_mode')

    if (primary && this.hasPrimaryAxisSelectTarget) this.primaryAxisSelectTarget.value = primary
    if (secondary && this.hasSecondaryAxisTarget) this.secondaryAxisTarget.value = secondary

    if (rollup && this.hasPropertyRollupHiddenTarget) {
      this.propertyRollupHiddenTarget.value = rollup
      if (this.hasPropertyRollupToggleTarget) {
        this.propertyRollupToggleTarget.checked = rollup === 'internal_external'
      }
    }

    const primaryPresent = q.get('primary_values_present') === '1'
    const secondaryPresent = q.get('secondary_values_present') === '1'

    const getAll = (k1, k2) => {
      const a = q.getAll(k1)
      if (a && a.length) return a
      const b = q.getAll(k2)
      return (b && b.length) ? b : []
    }

    if (primaryPresent) {
      this.selectedPrimaryValues = new Set(getAll('primary_values[]', 'primary_values').map(String))
    }
    if (secondaryPresent) {
      this.selectedSecondaryValues = new Set(getAll('secondary_values[]', 'secondary_values').map(String))
    }
  }

  disconnect() {
    this.formTarget.removeEventListener('change', this.onChange)
    if (this.monthlyChart) this.monthlyChart.destroy()
    if (this.cohortChart) this.cohortChart.destroy()
    if (this.varianceVizChart) this.varianceVizChart.destroy()
  }

  fetchData() {
    if (!this.hasFormTarget) {
      console.warn('[ReleaseVariance] fetchData called but formTarget missing')
      return
    }
    let query = new URLSearchParams(new FormData(this.formTarget))
    query = this.normalizeParams(query)

    // Ensure the request always reflects the current pill selections, even if the DOM
    // hidden inputs somehow get dropped by the browser/HTML parser.
    query.delete('primary_values_present')
    query.delete('primary_values[]')
    query.set('primary_values_present', '1')
    for (const v of Array.from(this.selectedPrimaryValues?.values?.() || [])) {
      query.append('primary_values[]', v)
    }

    query.delete('secondary_values_present')
    query.delete('secondary_values[]')
    if (this.secondaryAxisValue && this.secondaryAxisValue !== 'none') {
      query.set('secondary_values_present', '1')
      for (const v of Array.from(this.selectedSecondaryValues?.values?.() || [])) {
        query.append('secondary_values[]', v)
      }
    }

    console.log('[ReleaseVariance] fetching json with query:', query.toString())
    this.showOverlay(true)
    fetch(`${this.urlValue}.json?${query.toString()}`, { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(json => {
        console.log('[ReleaseVariance] fetch success', {
          monthly: json.monthly?.length,
          cohorts: json.cohorts?.length
        })
        this.monthlyData = json.monthly
        this.cohortData = json.cohorts
        this.varianceVizData = json.variance_viz
        this.axisValueCounts = this.varianceVizData?.axis_value_counts || {}
        this.updateCharts()
        this.renderVarianceViz()
        // Re-render pills with disabled/has-data state (and prune zero-count selections)
        this.syncPrimaryPills(false)
        this.syncSecondaryPills(false)
        // Update URL to reflect current filters without reloading
        const url = new URL(window.location.href)
        url.search = query.toString()
        window.history.replaceState({}, '', url.toString())
        console.log('[ReleaseVariance] charts updated and URL replaced')
      })
      .catch(err => {
        console.error('Failed to load data', err)
      })
      .finally(() => this.showOverlay(false))
  }

  showOverlay(show) {
    if (!this.hasOverlayTarget) return
    const targets = this.overlayTargets || [this.overlayTarget]
    targets.forEach(el => el.classList.toggle('hidden', !show))
    console.log('[ReleaseVariance] overlay', show ? 'show' : 'hide')
  }

  buildCharts() {
    console.log('[ReleaseVariance] buildCharts')
    const monthLabels = this.monthlyData.map(m => m.label)
    const monthValues = this.monthlyData.map(m => m.avg_months)
    const zeroBand = monthValues.map(v => (Math.abs(Number(v)) < 1e-6 ? [-0.5, 0.5] : null))
    console.log('[ReleaseVariance] monthly initial', { labels: monthLabels.length })

    this.monthlyChart = new Chart(this.monthlyCtx, {
      type: 'bar',
      plugins: [this.reissueShadingPlugin()],
      data: {
        labels: monthLabels,
        datasets: [
          {
            label: 'Average months vs estimate',
            data: monthValues,
            backgroundColor: monthValues.map(v => v >= 0 ? 'rgba(239, 68, 68, 0.6)' : 'rgba(34, 197, 94, 0.6)'),
            borderColor: monthValues.map(v => v >= 0 ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)'),
            borderWidth: 1
          },
          {
            label: 'Zero band',
            data: zeroBand,
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgb(59, 130, 246)',
            grouped: false,
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        // Track pointer position so the card fan can be positioned around the cursor
        // (Chart.js tooltip caretY tends to stick to the top of the bar).
        onHover: (event, _elements, chart) => {
          if (event && typeof event.x === 'number' && typeof event.y === 'number') {
            this._monthlyPointer = { x: event.x, y: event.y }
          }
          this.showReissueReadout(chart, event)
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (context) => this.renderMonthlyCardsTooltip(context)
          }
        },
        scales: {
          y: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } },
          x: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } }
        }
      }
    })

    // Ensure we hide the overlay when leaving the canvas (Chart.js doesn't always emit an external tooltip update).
    this.monthlyCanvasTarget.addEventListener('mouseleave', () => {
      this._monthlyPointer = null
      this._monthlyFanDir = null
      this.hideMonthlyCardsTooltip()
    })

    // --- New monthly counts chart (early/on-time/late) ---
    this.monthlyCountsCtx = this.monthlyCountsCanvasTarget.getContext('2d')
    const countLabels = this.monthlyData.map(m => m.label)
    const counts = this.monthlyData.map(m => m.counts || {})
    const earlyCounts = counts.map(c => Number(c.early) || 0)
    const onCounts = counts.map(c => Number(c.on_time) || 0)
    const lateCounts = counts.map(c => Number(c.late) || 0)

    const onHalf = onCounts.map(v => v / 2)
    const earlyRanges = earlyCounts.map((e, i) => (e > 0 ? [-(onHalf[i] + e), -onHalf[i]] : null))
    const onRanges = onCounts.map((o, i) => (o > 0 ? [-onHalf[i], onHalf[i]] : null))
    const lateRanges = lateCounts.map((l, i) => (l > 0 ? [onHalf[i], onHalf[i] + l] : null))

    this.monthlyCountsChart = new Chart(this.monthlyCountsCtx, {
      type: 'bar',
      plugins: [this.reissueShadingPlugin()],
      data: {
        labels: countLabels,
        datasets: [
          {
            label: 'Early',
            data: earlyRanges,
            grouped: false,
            backgroundColor: 'rgba(34, 197, 94, 0.65)',
            borderColor: 'rgb(34, 197, 94)',
            borderWidth: 1,
            borderSkipped: false
          },
          {
            label: 'On time',
            data: onRanges,
            grouped: false,
            backgroundColor: 'rgba(59, 130, 246, 0.65)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1,
            borderSkipped: false
          },
          {
            label: 'Late',
            data: lateRanges,
            grouped: false,
            backgroundColor: 'rgba(239, 68, 68, 0.65)',
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 1,
            borderSkipped: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        categoryPercentage: 0.92,
        barPercentage: 0.92,
        // Track pointer position for correct anchoring.
        onHover: (event, _elements, chart) => {
          if (event && typeof event.x === 'number' && typeof event.y === 'number') {
            this._monthlyPointer = { x: event.x, y: event.y }
          }
          this.showReissueReadout(chart, event)
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (context) => this.renderMonthlyCountsCardsTooltip(context)
          }
        },
        scales: {
          y: {
            grid: { color: 'rgba(75, 85, 99, 0.2)' },
            ticks: {
              color: '#9CA3AF',
              callback: (v) => {
                const n = Number(v)
                return Number.isFinite(n) ? Math.abs(n) : v
              }
            }
          },
          x: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } }
        }
      }
    })

    this.monthlyCountsCanvasTarget.addEventListener('mouseleave', () => {
      this._monthlyPointer = null
      this._monthlyFanDir = null
      this.hideMonthlyCardsTooltip()
    })

    const cohortLabels = this.cohortData.map(c => c.label)
    const cohortAvg = this.cohortData.map(c => c.avg_months)
    const cohortMedian = this.cohortData.map(c => c.median_months)
    // Don't draw a line segment into the final "All" bar (it's a summary, not a continuation).
    const cohortPercentLate = this.cohortData.map(c => (c.label === 'All' ? null : c.percent_late))
    const allIdx = cohortLabels.indexOf('All')
    const allPct = allIdx >= 0 ? (this.cohortData[allIdx]?.percent_late ?? null) : null
    const cohortPercentLateAllPoint = cohortLabels.map((_, i) => (i === allIdx ? allPct : null))
    console.log('[ReleaseVariance] cohort initial', { labels: cohortLabels.length })

    this.cohortChart = new Chart(this.cohortCtx, {
      type: 'bar',
      data: {
        labels: cohortLabels,
        datasets: [
          {
            label: 'Average months',
            data: cohortAvg,
            backgroundColor: cohortAvg.map(v => v >= 0 ? 'rgba(239, 68, 68, 0.6)' : 'rgba(34, 197, 94, 0.6)'),
            borderColor: cohortAvg.map(v => v >= 0 ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)'),
            borderWidth: 1
          },
          {
            label: 'Median months',
            data: cohortMedian,
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgb(59, 130, 246)',
            borderWidth: 1
          },
          {
            label: '% Late',
            data: cohortPercentLate,
            yAxisID: 'y1',
            type: 'line',
            borderColor: 'rgb(234, 179, 8)',
            backgroundColor: 'rgba(234, 179, 8, 0.2)'
          },
          {
            label: '% Late (All)',
            data: cohortPercentLateAllPoint,
            yAxisID: 'y1',
            type: 'line',
            showLine: false,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderColor: 'rgb(234, 179, 8)',
            backgroundColor: 'rgb(234, 179, 8)'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        plugins: {
          legend: {
            labels: {
              filter: (item) => item.text !== '% Late (All)'
            }
          },
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const idx = items[0].dataIndex
                const cohort = this.cohortData[idx]
                if (!cohort || !cohort.items) return ''
                const lines = cohort.items.map(it => {
                  const title = it.variation ? `${it.name} — ${it.variation}` : it.name
                  const expected = (it.expected_start || it.expected_end)
                    ? ` (Est: ${this.formatDate(it.expected_start)}–${this.formatDate(it.expected_end)})`
                    : ''
                  return `${title} — Δ: ${this.formatDelta(it.months_from_estimate)}${expected}`
                })
                return lines.join('\n')
              }
            }
          }
        },
        scales: {
          y: { position: 'left', grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } },
          y1: { position: 'right', grid: { drawOnChartArea: false }, ticks: { color: '#EAB308', callback: (v) => v + '%' }, min: 0, max: 100 },
          x: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } }
        }
      }
    })

    // Initial alignment of zero lines between y and y1
    this.alignDualAxisZero(this.cohortChart, cohortAvg, cohortMedian)
  }

  // Shades each release month that had reissues in production behind its bar, darker for more of them.
  reissueShadingPlugin() {
    return {
      id: 'reissueShading',
      beforeDatasetsDraw: (chart) => {
        const { ctx, chartArea, scales: { x } } = chart
        const step = x.width / Math.max(this.monthlyData.length, 1)
        ctx.save()
        this.monthlyData.forEach((m, i) => {
          const n = (m.reissues_in_production || []).length
          if (n === 0) return
          ctx.fillStyle = `rgba(167, 139, 250, ${Math.min(0.08 + 0.06 * n, 0.4)})`
          ctx.fillRect(x.getPixelForValue(i) - step / 2, chartArea.top, step, chartArea.bottom - chartArea.top)
        })
        ctx.restore()
      }
    }
  }

  // Under the monthly chart: the reissues in production in the month under the pointer.
  showReissueReadout(chart, event) {
    if (!this.hasReissueReadoutTarget) return
    const x = chart.scales.x
    const i = event && typeof event.x === 'number' ? Math.round(x.getValueForPixel(event.x)) : null
    const month = (i !== null && i >= 0) ? this.monthlyData[i] : null
    const names = month?.reissues_in_production || []
    this.reissueReadoutTarget.textContent = !month ? '' : names.length
      ? `${month.label}: ${names.length} reissue${names.length === 1 ? '' : 's'} in production (${names.join(', ')})`
      : `${month.label}: no reissues in production`
  }

  hideMonthlyCardsTooltip() {
    if (!this._monthlyCardsPortal) return
    this._monthlyCardsPortal.style.display = 'none'
    this._monthlyCardsPortal.innerHTML = ''
  }

  renderMonthlyCardsTooltip(context) {
    if (!this._monthlyCardsPortal || !this.hasFigureCardTemplateTarget) return

    const { tooltip } = context || {}
    if (!tooltip || tooltip.opacity === 0) {
      this.hideMonthlyCardsTooltip()
      return
    }

    const dp = tooltip.dataPoints && tooltip.dataPoints[0]
    const idx = dp ? dp.dataIndex : null
    const month = typeof idx === 'number' ? this.monthlyData[idx] : null
    const items = Array.isArray(month?.items) ? month.items : []

    const host = this._monthlyCardsPortal
    host.style.display = 'block'
    host.innerHTML = ''

    // Anchor point in VIEWPORT coordinates.
    // Chart.js provides canvas-local x/y. Convert to viewport via getBoundingClientRect().
    const rect = this.monthlyCanvasTarget.getBoundingClientRect()
    const localX = (this._monthlyPointer && typeof this._monthlyPointer.x === 'number') ? this._monthlyPointer.x : (Number(tooltip.caretX) || 0)
    const localY = (this._monthlyPointer && typeof this._monthlyPointer.y === 'number') ? this._monthlyPointer.y : (Number(tooltip.caretY) || 0)
    const anchorX = rect.left + localX
    const anchorY = rect.top + localY

    const n = Math.min(items.length, 20)
    if (n === 0) return

    // Fan around the cursor, "pointing" away from edges.
    // Instead of rotating the whole fan arbitrarily, we pick one of four directions:
    // up / right / left / down, based on which option best stays inside the container.
    //
    // Card tilt is clamped to ±30° so nothing goes sideways/upside-down.
    const desiredArcStepPx = 120 // spacing between adjacent card centers along the arc
    const fanRadius = 155        // distance of cards from cursor
    const maxSpreadDeg = 140     // keep a pleasant fan; avoid wrapping too far
    const rotClampDeg = 30
    const rotStepDeg = 10

    const cardW = 128
    const cardH = 150
    const pad = 10
    const safety = 18 // approximate extra due to rotation/shadow

    const spreadRadUncapped = (desiredArcStepPx / Math.max(fanRadius, 1)) * Math.max(0, (n - 1))
    const spreadRad = Math.min((maxSpreadDeg * Math.PI) / 180, spreadRadUncapped)

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

    const computeOffsetsForPhi = (phiDeg) => {
      const phi = (phiDeg * Math.PI) / 180
      const pts = []
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

      for (let i = 0; i < n; i++) {
        const frac = n === 1 ? 0.5 : (i / (n - 1))
        const rel = (0.5 - frac) * spreadRad
        const ang = phi + rel
        const dx = Math.cos(ang) * fanRadius
        const dy = -Math.sin(ang) * fanRadius

        const center = (n - 1) / 2
        const rot = clamp((i - center) * rotStepDeg, -rotClampDeg, rotClampDeg)

        pts.push({ dx, dy, rot })

        const left = anchorX + dx - (cardW / 2) - safety
        const right = anchorX + dx + (cardW / 2) + safety
        const top = anchorY + dy - (cardH / 2) - safety
        const bottom = anchorY + dy + (cardH / 2) + safety
        minX = Math.min(minX, left)
        maxX = Math.max(maxX, right)
        minY = Math.min(minY, top)
        maxY = Math.max(maxY, bottom)
      }

      return { pts, bbox: { minX, minY, maxX, maxY } }
    }

    const overflowScore = (bbox, w, h) => {
      const overL = Math.max(0, pad - bbox.minX)
      const overT = Math.max(0, pad - bbox.minY)
      const overR = Math.max(0, bbox.maxX - (w - pad))
      const overB = Math.max(0, bbox.maxY - (h - pad))
      return overL * overL + overT * overT + overR * overR + overB * overB
    }

    const w = window.innerWidth || 0
    const h = window.innerHeight || 0

    // Prefer up (12 o'clock) when it fits; otherwise choose the best-fitting direction.
    const candidates = [
      { phi: 90, penalty: 0 },   // up
      { phi: 0, penalty: 4 },    // right
      { phi: 180, penalty: 4 },  // left
      { phi: -90, penalty: 8 }   // down (least preferred)
    ]

    let best = null
    let upCandidate = null
    for (const c of candidates) {
      const o = computeOffsetsForPhi(c.phi)
      const over = (w > 0 && h > 0) ? overflowScore(o.bbox, w, h) : 0
      const score = over + c.penalty
      const cand = { score, phi: c.phi, offsets: o, over }
      if (c.phi === 90) upCandidate = cand
      if (!best || score < best.score) best = cand
    }

    // Critical behavior:
    // - Always snap back to "up" when it fits (prevents the “stuck on one side” bug).
    // - Only use left/right/down when up would overflow.
    let chosen = best
    if (upCandidate && upCandidate.over === 0) chosen = upCandidate

    // Mild hysteresis only when we are NOT in the safe-up case:
    // keep current direction if it also avoids overflow.
    if (chosen?.phi !== 90 && this._monthlyFanDir) {
      const cur = computeOffsetsForPhi(this._monthlyFanDir)
      const curOver = (w > 0 && h > 0) ? overflowScore(cur.bbox, w, h) : 0
      if (curOver === 0) {
        chosen = { score: curOver, phi: this._monthlyFanDir, offsets: cur, over: curOver }
      }
    }

    this._monthlyFanDir = chosen?.phi || 90
    const offsets = chosen ? chosen.offsets : computeOffsetsForPhi(90)

    for (let i = 0; i < n; i++) {
      const it = items[i] || {}
      const p = offsets.pts[i]

      const card = this.buildMonthlyFigureCard(it)
      card.style.left = `${anchorX}px`
      card.style.top = `${anchorY}px`
      card.style.transform = `translate(-50%, -50%) translate(${p.dx.toFixed(1)}px, ${p.dy.toFixed(1)}px) rotate(${p.rot.toFixed(1)}deg)`
      card.style.zIndex = String(100 + i)
      host.appendChild(card)
    }
  }

  buildMonthlyFigureCard(it) {
    const card = this.figureCardTemplateTarget.content.firstElementChild.cloneNode(true)

    const titleEl = card.querySelector('[data-card-title]')
    const deltaEl = card.querySelector('[data-card-delta]')
    const poEl = card.querySelector('[data-card-po]')
    const estEl = card.querySelector('[data-card-est]')
    const relEl = card.querySelector('[data-card-release]')
    const imgEl = card.querySelector('[data-card-image]')
    const placeholderEl = card.querySelector('[data-card-placeholder]')
    const imgWrapEl = card.querySelector('[data-card-image-wrap]')

    const name = String(it.name || '')
    const variation = String(it.variation || '')
    const title = variation ? `${name} — ${variation}` : name

    const m = Number(it.months_from_estimate) || 0
    const deltaText = `Δ: ${this.formatDelta(m)}`

    if (titleEl) titleEl.textContent = title
    if (deltaEl) {
      deltaEl.textContent = deltaText
      if (m > 0) deltaEl.classList.add('text-red-300')
      else if (m < 0) deltaEl.classList.add('text-green-300')
      else deltaEl.classList.add('text-blue-300')
    }

    if (poEl) poEl.textContent = `PO: ${this.formatDate(it.pre_order_date)}`
    if (estEl) estEl.textContent = `Est: ${this.formatDate(it.expected_start)}–${this.formatDate(it.expected_end)}`
    if (relEl) relEl.textContent = `Release: ${this.formatDate(it.release_date)}`

    const imageUrl = it.image_url ? String(it.image_url) : ''
    if (imageUrl && imgEl) {
      if (imgWrapEl) {
        imgWrapEl.classList.add('loading')
        imgWrapEl.classList.remove('loaded')
      }
      imgEl.classList.add('hidden')
      // While loading, show shimmer only (no "No image" flash).
      if (placeholderEl) placeholderEl.classList.add('hidden')

      imgEl.onload = () => {
        if (imgWrapEl) {
          imgWrapEl.classList.remove('loading')
          imgWrapEl.classList.add('loaded')
        }
        imgEl.classList.remove('hidden')
        if (placeholderEl) placeholderEl.classList.add('hidden')
      }

      imgEl.onerror = () => {
        if (imgWrapEl) {
          imgWrapEl.classList.remove('loading')
          imgWrapEl.classList.add('loaded')
        }
        imgEl.classList.add('hidden')
        // Only show placeholder if we truly failed to load.
        if (placeholderEl) placeholderEl.classList.remove('hidden')
      }

      imgEl.alt = title
      imgEl.src = imageUrl
    } else {
      if (imgEl) imgEl.classList.add('hidden')
      if (placeholderEl) placeholderEl.classList.remove('hidden')
      if (imgWrapEl) {
        imgWrapEl.classList.remove('loading')
        imgWrapEl.classList.add('loaded')
      }
    }

    return card
  }

  updateCharts() {
    console.log('[ReleaseVariance] updateCharts')
    // Monthly
    const labels = this.monthlyData.map(m => m.label)
    const values = this.monthlyData.map(m => m.avg_months)
    const zeroBand = values.map(v => (Math.abs(Number(v)) < 1e-6 ? [-0.5, 0.5] : null))
    console.log('[ReleaseVariance] monthly update', { labels: labels.length })
    this.monthlyChart.data.labels = labels
    this.monthlyChart.data.datasets[0].data = values
    this.monthlyChart.data.datasets[0].backgroundColor = values.map(v => v >= 0 ? 'rgba(239, 68, 68, 0.6)' : 'rgba(34, 197, 94, 0.6)')
    this.monthlyChart.data.datasets[0].borderColor = values.map(v => v >= 0 ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)')
    if (this.monthlyChart.data.datasets[1]) {
      this.monthlyChart.data.datasets[1].data = zeroBand
    }
    this.monthlyChart.update()

    // Monthly counts chart
    if (this.monthlyCountsChart) {
      const labels2 = this.monthlyData.map(m => m.label)
      const counts2 = this.monthlyData.map(m => m.counts || {})
      const early2 = counts2.map(c => Number(c.early) || 0)
      const on2 = counts2.map(c => Number(c.on_time) || 0)
      const late2 = counts2.map(c => Number(c.late) || 0)
      const half2 = on2.map(v => v / 2)
      const earlyRanges2 = early2.map((e, i) => (e > 0 ? [-(half2[i] + e), -half2[i]] : null))
      const onRanges2 = on2.map((o, i) => (o > 0 ? [-half2[i], half2[i]] : null))
      const lateRanges2 = late2.map((l, i) => (l > 0 ? [half2[i], half2[i] + l] : null))

      this.monthlyCountsChart.data.labels = labels2
      this.monthlyCountsChart.data.datasets[0].data = earlyRanges2
      this.monthlyCountsChart.data.datasets[1].data = onRanges2
      this.monthlyCountsChart.data.datasets[2].data = lateRanges2
      this.monthlyCountsChart.update()
    }

    // Cohorts
    const cLabels = this.cohortData.map(c => c.label)
    const cAvg = this.cohortData.map(c => c.avg_months)
    const cMed = this.cohortData.map(c => c.median_months)
    const cPct = this.cohortData.map(c => (c.label === 'All' ? null : c.percent_late))
    const allIdx = cLabels.indexOf('All')
    const allPct = allIdx >= 0 ? (this.cohortData[allIdx]?.percent_late ?? null) : null
    const cPctAllPoint = cLabels.map((_, i) => (i === allIdx ? allPct : null))
    console.log('[ReleaseVariance] cohort update', { labels: cLabels.length })
    this.cohortChart.data.labels = cLabels
    this.cohortChart.data.datasets[0].data = cAvg
    this.cohortChart.data.datasets[0].backgroundColor = cAvg.map(v => v >= 0 ? 'rgba(239, 68, 68, 0.6)' : 'rgba(34, 197, 94, 0.6)')
    this.cohortChart.data.datasets[0].borderColor = cAvg.map(v => v >= 0 ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)')
    this.cohortChart.data.datasets[1].data = cMed
    this.cohortChart.data.datasets[2].data = cPct
    if (this.cohortChart.data.datasets[3]) this.cohortChart.data.datasets[3].data = cPctAllPoint
    // Align zeros again after data update
    this.alignDualAxisZero(this.cohortChart, cAvg, cMed)
    this.cohortChart.update()
    this.renderCohortTable()
  }

  showMonthlyAverage() {
    if (this.hasMonthlyAverageWrapTarget) this.monthlyAverageWrapTarget.classList.remove('hidden')
    if (this.hasMonthlyCountsWrapTarget) this.monthlyCountsWrapTarget.classList.add('hidden')

    if (this.hasMonthlySectionTitleTarget) {
      this.monthlySectionTitleTarget.textContent = 'Average Delay by Release Month'
    }
    if (this.hasMonthlyAverageToggleTarget) {
      this.monthlyAverageToggleTarget.classList.add('on')
      this.monthlyAverageToggleTarget.classList.remove('quiet')
      this.monthlyAverageToggleTarget.setAttribute('aria-pressed', 'true')
    }
    if (this.hasMonthlyCountsToggleTarget) {
      this.monthlyCountsToggleTarget.classList.add('quiet')
      this.monthlyCountsToggleTarget.classList.remove('on')
      this.monthlyCountsToggleTarget.setAttribute('aria-pressed', 'false')
    }
  }

  showMonthlyCounts() {
    if (this.hasMonthlyAverageWrapTarget) this.monthlyAverageWrapTarget.classList.add('hidden')
    if (this.hasMonthlyCountsWrapTarget) this.monthlyCountsWrapTarget.classList.remove('hidden')

    if (this.hasMonthlySectionTitleTarget) {
      this.monthlySectionTitleTarget.textContent = 'Early, On-time, and Late Figures by Release Month'
    }
    if (this.hasMonthlyAverageToggleTarget) {
      this.monthlyAverageToggleTarget.classList.add('quiet')
      this.monthlyAverageToggleTarget.classList.remove('on')
      this.monthlyAverageToggleTarget.setAttribute('aria-pressed', 'false')
    }
    if (this.hasMonthlyCountsToggleTarget) {
      this.monthlyCountsToggleTarget.classList.add('on')
      this.monthlyCountsToggleTarget.classList.remove('quiet')
      this.monthlyCountsToggleTarget.setAttribute('aria-pressed', 'true')
    }
  }

  renderMonthlyCountsCardsTooltip(context) {
    const { tooltip } = context || {}
    if (!tooltip || tooltip.opacity === 0) {
      this.hideMonthlyCardsTooltip()
      return
    }

    const dp = tooltip.dataPoints && tooltip.dataPoints[0]
    const idx = dp ? dp.dataIndex : null
    const dsIdx = dp ? dp.datasetIndex : null
    if (typeof idx !== 'number' || typeof dsIdx !== 'number') return

    const month = this.monthlyData[idx]
    const byBucket = month?.items_by_bucket || {}

    const bucket =
      dsIdx === 0 ? 'early' :
      dsIdx === 1 ? 'on_time' :
      dsIdx === 2 ? 'late' : null

    const items = bucket ? (byBucket[bucket] || []) : []
    return this.renderCardsTooltipForItems(context, this.monthlyCountsCanvasTarget, items)
  }

  renderCardsTooltipForItems(context, canvasEl, items) {
    if (!this._monthlyCardsPortal || !this.hasFigureCardTemplateTarget) return
    const { tooltip } = context || {}
    if (!tooltip || tooltip.opacity === 0) {
      this.hideMonthlyCardsTooltip()
      return
    }

    const host = this._monthlyCardsPortal
    host.style.display = 'block'
    host.innerHTML = ''

    const rect = canvasEl.getBoundingClientRect()
    const localX = (this._monthlyPointer && typeof this._monthlyPointer.x === 'number') ? this._monthlyPointer.x : (Number(tooltip.caretX) || 0)
    const localY = (this._monthlyPointer && typeof this._monthlyPointer.y === 'number') ? this._monthlyPointer.y : (Number(tooltip.caretY) || 0)
    const anchorX = rect.left + localX
    const anchorY = rect.top + localY

    const n = Math.min(Array.isArray(items) ? items.length : 0, 20)
    if (n === 0) return

    // Same fan layout + viewport-aware direction choice as the monthly average chart.
    const desiredArcStepPx = 120
    const fanRadius = 155
    const maxSpreadDeg = 140
    const rotClampDeg = 30
    const rotStepDeg = 10
    const cardW = 128
    const cardH = 150
    const pad = 10
    const safety = 18

    const spreadRadUncapped = (desiredArcStepPx / Math.max(fanRadius, 1)) * Math.max(0, (n - 1))
    const spreadRad = Math.min((maxSpreadDeg * Math.PI) / 180, spreadRadUncapped)
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

    const computeOffsetsForPhi = (phiDeg) => {
      const phi = (phiDeg * Math.PI) / 180
      const pts = []
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      for (let i = 0; i < n; i++) {
        const frac = n === 1 ? 0.5 : (i / (n - 1))
        const rel = (0.5 - frac) * spreadRad
        const ang = phi + rel
        const dx = Math.cos(ang) * fanRadius
        const dy = -Math.sin(ang) * fanRadius
        const center = (n - 1) / 2
        const rot = clamp((i - center) * rotStepDeg, -rotClampDeg, rotClampDeg)
        pts.push({ dx, dy, rot })
        const left = anchorX + dx - (cardW / 2) - safety
        const right = anchorX + dx + (cardW / 2) + safety
        const top = anchorY + dy - (cardH / 2) - safety
        const bottom = anchorY + dy + (cardH / 2) + safety
        minX = Math.min(minX, left)
        maxX = Math.max(maxX, right)
        minY = Math.min(minY, top)
        maxY = Math.max(maxY, bottom)
      }
      return { pts, bbox: { minX, minY, maxX, maxY } }
    }

    const overflowScore = (bbox, w, h) => {
      const overL = Math.max(0, pad - bbox.minX)
      const overT = Math.max(0, pad - bbox.minY)
      const overR = Math.max(0, bbox.maxX - (w - pad))
      const overB = Math.max(0, bbox.maxY - (h - pad))
      return overL * overL + overT * overT + overR * overR + overB * overB
    }

    const w = window.innerWidth || 0
    const h = window.innerHeight || 0
    const candidates = [
      { phi: 90, penalty: 0 },
      { phi: 0, penalty: 4 },
      { phi: 180, penalty: 4 },
      { phi: -90, penalty: 8 }
    ]
    let best = null
    let upCandidate = null
    for (const c of candidates) {
      const o = computeOffsetsForPhi(c.phi)
      const over = (w > 0 && h > 0) ? overflowScore(o.bbox, w, h) : 0
      const score = over + c.penalty
      const cand = { score, phi: c.phi, offsets: o, over }
      if (c.phi === 90) upCandidate = cand
      if (!best || score < best.score) best = cand
    }

    let chosen = best
    if (upCandidate && upCandidate.over === 0) chosen = upCandidate

    if (chosen?.phi !== 90 && this._monthlyFanDir) {
      const cur = computeOffsetsForPhi(this._monthlyFanDir)
      const curOver = (w > 0 && h > 0) ? overflowScore(cur.bbox, w, h) : 0
      if (curOver === 0) {
        chosen = { score: curOver, phi: this._monthlyFanDir, offsets: cur, over: curOver }
      }
    }

    this._monthlyFanDir = chosen?.phi || 90
    const offsets = chosen ? chosen.offsets : computeOffsetsForPhi(90)

    for (let i = 0; i < n; i++) {
      const it = items[i] || {}
      const p = offsets.pts[i]
      const card = this.buildMonthlyFigureCard(it)
      card.style.left = `${anchorX}px`
      card.style.top = `${anchorY}px`
      card.style.transform = `translate(-50%, -50%) translate(${p.dx.toFixed(1)}px, ${p.dy.toFixed(1)}px) rotate(${p.rot.toFixed(1)}deg)`
      card.style.zIndex = String(100 + i)
      host.appendChild(card)
    }
  }

  // --- Third viz: axis config + pills ---
  // Mezco's own properties (original IP), from the page (Property.original_names).
  staticInternalProperties() {
    return new Set(this.internalPropertiesValue)
  }

  syncPrimaryAxisUIFromDOM() {
    this.primaryAxis = this.hasPrimaryAxisSelectTarget ? this.primaryAxisSelectTarget.value : 'licensing_property'
  }

  primaryAxisChanged() {
    this.syncPrimaryAxisUIFromDOM()
    this.syncSecondaryOptions()
    this.syncPropertyRollupVisibility()
    this.syncPrimaryPills(true)
    this.syncSecondaryPills(true)
    this.fetchData()
  }

  secondaryAxisChanged() {
    this.secondaryAxisValue = this.secondaryAxisTarget.value
    this.syncSecondaryPills(true)
    this.fetchData()
  }

  propertyRollupChanged() {
    const enabled = !!this.propertyRollupToggleTarget.checked
    this.propertyRollupHiddenTarget.value = enabled ? 'internal_external' : 'none'
    this.fetchData()
  }

  syncPropertyRollupVisibility() {
    const show = this.primaryAxis === 'licensing_property'
    this.propertyRollupRowTarget.classList.toggle('hidden', !show)
    if (!show) {
      this.propertyRollupToggleTarget.checked = false
      this.propertyRollupHiddenTarget.value = 'none'
    }
  }

  syncSecondaryOptions() {
    // Disable choosing the same axis as the primary
    const primary = this.primaryAxis
    const select = this.secondaryAxisTarget
    Array.from(select.options).forEach((opt) => {
      if (opt.value === primary) {
        opt.disabled = true
        if (select.value === primary) select.value = 'none'
      } else {
        opt.disabled = false
      }
    })
    this.secondaryAxisValue = select.value
    this.secondaryPillsSectionTarget.classList.toggle('hidden', this.secondaryAxisValue === 'none')
  }

  optionsForAxis(axis) {
    const opts = this.factorOptionsData?.[axis]
    return Array.isArray(opts) ? opts.map(String) : []
  }

  countFor(axis, value) {
    const a = String(axis || '')
    const v = String(value || '')
    return Number(this.axisValueCounts?.[a]?.[v] || 0)
  }

  enabledOptionsForAxis(axis, options) {
    // If we don't have counts yet, treat all as enabled.
    if (!this.axisValueCounts || Object.keys(this.axisValueCounts).length === 0) return options
    return options.filter(v => this.countFor(axis, v) > 0)
  }

  pruneSelectedToEnabled(axis, options, selectedSet) {
    const enabled = new Set(this.enabledOptionsForAxis(axis, options).map(String))
    for (const v of Array.from(selectedSet.values())) {
      if (!enabled.has(String(v))) selectedSet.delete(v)
    }
  }

  syncPrimaryPills(resetAll) {
    const opts = this.optionsForAxis(this.primaryAxis)
    if (resetAll) this.selectedPrimaryValues = new Set(this.enabledOptionsForAxis(this.primaryAxis, opts))
    this.pruneSelectedToEnabled(this.primaryAxis, opts, this.selectedPrimaryValues)
    this.renderPills(this.primaryPillsTarget, opts, this.selectedPrimaryValues, this.primaryAxis, (v, btn) => this.togglePrimary(v, btn))
    this.writeHiddenInputs(this.primaryHiddenInputsTarget, 'primary_values_present', 'primary_values[]', this.selectedPrimaryValues)
  }

  syncSecondaryPills(resetAll) {
    this.syncSecondaryOptions()
    if (this.secondaryAxisValue === 'none') {
      this.secondaryPillsTarget.innerHTML = ''
      this.secondaryHiddenInputsTarget.innerHTML = ''
      this.selectedSecondaryValues = new Set()
      return
    }
    const opts = this.optionsForAxis(this.secondaryAxisValue)
    if (resetAll) this.selectedSecondaryValues = new Set(this.enabledOptionsForAxis(this.secondaryAxisValue, opts))
    this.pruneSelectedToEnabled(this.secondaryAxisValue, opts, this.selectedSecondaryValues)
    this.renderPills(this.secondaryPillsTarget, opts, this.selectedSecondaryValues, this.secondaryAxisValue, (v, btn) => this.toggleSecondary(v, btn))
    this.writeHiddenInputs(this.secondaryHiddenInputsTarget, 'secondary_values_present', 'secondary_values[]', this.selectedSecondaryValues)
  }

  pillClass(_axis, _val, selected, disabled) {
    // Standard pill styling (blue selected, gray unselected)
    const base = selected
      ? 'px-3 py-1 rounded-full text-xs bg-blue-600 text-white border border-blue-500 hover:bg-blue-500'
      : 'px-3 py-1 rounded-full text-xs bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700'
    if (!disabled) return base
    return selected
      ? 'px-3 py-1 rounded-full text-xs bg-blue-600/30 text-gray-300 border border-gray-600 cursor-not-allowed opacity-60'
      : 'px-3 py-1 rounded-full text-xs bg-gray-800/40 text-gray-400 border border-gray-700 cursor-not-allowed opacity-60'
  }

  renderPills(container, options, selectedSet, axis, onToggle) {
    container.innerHTML = ''

    // For licensing_property, show internal/external sections for display only.
    if (axis === 'licensing_property') {
      const internalSet = this.staticInternalProperties()
      const internal = options.filter(v => internalSet.has(String(v)))
      const external = options.filter(v => !internalSet.has(String(v)))

      const makeSection = (title) => {
        const section = document.createElement('div')
        section.className = 'w-full'
        const h = document.createElement('div')
        h.className = 'text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2'
        h.textContent = title
        const pills = document.createElement('div')
        pills.className = 'flex flex-wrap gap-2'
        section.appendChild(h)
        section.appendChild(pills)
        return { section, pills }
      }

      if (internal.length > 0) {
        const { section, pills } = makeSection('Internal')
        internal.forEach((val) => this.appendPillButton(pills, val, selectedSet, axis, onToggle))
        container.appendChild(section)
      }
      if (external.length > 0) {
        const { section, pills } = makeSection('External')
        external.forEach((val) => this.appendPillButton(pills, val, selectedSet, axis, onToggle))
        container.appendChild(section)
      }
      return
    }

    for (const val of options) {
      this.appendPillButton(container, val, selectedSet, axis, onToggle)
    }
  }

  appendPillButton(container, val, selectedSet, axis, onToggle) {
    const haveCounts = this.axisValueCounts && Object.keys(this.axisValueCounts).length > 0
    const disabled = haveCounts && this.countFor(axis, val) === 0
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.textContent = val
    btn.className = this.pillClass(axis, val, selectedSet.has(val), disabled)
    btn.disabled = disabled
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false')
    if (!disabled) btn.addEventListener('click', () => onToggle(val, btn))
    container.appendChild(btn)
  }

  writeHiddenInputs(container, presentFlagName, name, selectedSet) {
    container.innerHTML = ''
    const present = document.createElement('input')
    present.type = 'hidden'
    present.name = presentFlagName
    present.value = '1'
    container.appendChild(present)
    for (const v of Array.from(selectedSet.values())) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = v
      container.appendChild(input)
    }
  }

  togglePrimary(val, btn) {
    if (this.selectedPrimaryValues.has(val)) this.selectedPrimaryValues.delete(val)
    else this.selectedPrimaryValues.add(val)
    btn.className = this.pillClass(this.primaryAxis, val, this.selectedPrimaryValues.has(val), false)
    this.writeHiddenInputs(this.primaryHiddenInputsTarget, 'primary_values_present', 'primary_values[]', this.selectedPrimaryValues)
    // Ensure chart always reflects the current selection immediately
    this.fetchData()
  }

  toggleSecondary(val, btn) {
    if (this.selectedSecondaryValues.has(val)) this.selectedSecondaryValues.delete(val)
    else this.selectedSecondaryValues.add(val)
    btn.className = this.pillClass(this.secondaryAxisValue, val, this.selectedSecondaryValues.has(val), false)
    this.writeHiddenInputs(this.secondaryHiddenInputsTarget, 'secondary_values_present', 'secondary_values[]', this.selectedSecondaryValues)
    // Ensure chart always reflects the current selection immediately
    this.fetchData()
  }

  selectAllPrimary() {
    const opts = this.optionsForAxis(this.primaryAxis)
    this.selectedPrimaryValues = new Set(this.enabledOptionsForAxis(this.primaryAxis, opts))
    this.syncPrimaryPills(false)
    this.fetchData()
  }
  selectNonePrimary() {
    this.selectedPrimaryValues = new Set()
    this.syncPrimaryPills(false)
    this.fetchData()
  }
  selectAllSecondary() {
    if (this.secondaryAxisValue === 'none') return
    const opts = this.optionsForAxis(this.secondaryAxisValue)
    this.selectedSecondaryValues = new Set(this.enabledOptionsForAxis(this.secondaryAxisValue, opts))
    this.syncSecondaryPills(false)
    this.fetchData()
  }
  selectNoneSecondary() {
    if (this.secondaryAxisValue === 'none') return
    this.selectedSecondaryValues = new Set()
    this.syncSecondaryPills(false)
    this.fetchData()
  }

  // --- Third viz: rendering ---
  renderVarianceViz() {
    if (!this.varianceVizData) return
    if (this.varianceVizChart) {
      try { this.varianceVizChart.destroy() } catch (_) {}
      this.varianceVizChart = null
    }

    const mode = this.varianceVizData.mode
    if (mode === 'boxplot') {
      this.renderBoxplot(this.varianceVizData)
    } else if (mode === 'matrix_scatter') {
      this.renderMatrixScatter(this.varianceVizData)
    }
  }

  renderBoxplot(payload) {
    const boxes = Array.isArray(payload.boxplots) ? payload.boxplots : []
    const labels = boxes.map(b => b.label)
    const data = boxes.map(b => ({
      min: b.stats?.min ?? 0,
      q1: b.stats?.q1 ?? 0,
      median: b.stats?.median ?? 0,
      q3: b.stats?.q3 ?? 0,
      max: b.stats?.max ?? 0,
      whiskerMin: b.stats?.min ?? 0,
      whiskerMax: b.stats?.max ?? 0,
      mean: b.stats?.mean ?? b.stats?.avg ?? 0
    }))

    this.varianceVizChart = new Chart(this.varianceVizCtx, {
      type: 'boxplot',
      data: {
        labels,
        datasets: [{
          label: 'Release variance (months): min / q1 / median / q3 / max (box+whiskers); mean in tooltip',
          data,
          backgroundColor: (ctx) => this.varianceGradient(ctx.chart, 0.22),
          borderColor: (ctx) => this.varianceGradient(ctx.chart, 1.0),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            displayColors: false,
            callbacks: {
              // Avoid repeating label text; we only show the stats lines below.
              title: () => [],
              label: () => '',
              afterBody: (items) => {
                const idx = items[0].dataIndex
                const b = boxes[idx]
                if (!b) return ''
                const s = b.stats || {}
                const lines = [
                  `count: ${b.count ?? 0}`,
                  `mean: ${s.mean ?? s.avg ?? 0}`,
                  `min: ${s.min ?? 0}`,
                  `q1: ${s.q1 ?? 0}`,
                  `median: ${s.median ?? 0}`,
                  `q3: ${s.q3 ?? 0}`,
                  `max: ${s.max ?? 0}`,
                  `% late: ${s.percent_late ?? 0}%`
                ]
                return lines
              }
            }
          }
        },
        scales: {
          y: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } },
          x: { grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } }
        }
      }
    })
  }

  varianceGradient(chart, alpha = 1.0) {
    const { ctx, chartArea, scales } = chart
    if (!chartArea || !scales?.y) return `rgba(59, 130, 246, ${alpha})`

    const y = scales.y
    const top = chartArea.top
    const bottom = chartArea.bottom
    const g = ctx.createLinearGradient(0, bottom, 0, top)

    const clamp01 = (v) => Math.max(0, Math.min(1, v))
    const posForValue = (v) => clamp01((y.getPixelForValue(v) - bottom) / (top - bottom))

    // Align color stops to the Y-axis values:
    // green for negative, blue around -0.5..0.5, red for positive.
    // User-defined extremes: full green at -2, full red at +2
    const pNegExtreme = posForValue(-2)
    const pZeroLow = posForValue(-0.5)
    const pZeroHigh = posForValue(0.5)
    const pPosExtreme = posForValue(2)

    const green = `rgba(34, 197, 94, ${alpha})`
    const blue = `rgba(59, 130, 246, ${alpha})`
    const red = `rgba(239, 68, 68, ${alpha})`

    g.addColorStop(0, green)
    g.addColorStop(pNegExtreme, green)
    g.addColorStop(pZeroLow, blue)
    g.addColorStop(pZeroHigh, blue)
    g.addColorStop(pPosExtreme, red)
    g.addColorStop(1, red)
    return g
  }

  renderMatrixScatter(payload) {
    const cells = Array.isArray(payload.cells) ? payload.cells : []
    const xLabels = Array.isArray(payload.x_labels) ? payload.x_labels.map(String) : []
    const yLabels = Array.isArray(payload.y_labels) ? payload.y_labels.map(String) : []
    const maxAbs = Math.max(Number(payload.max_abs_avg_months) || 0, 1)

    const points = cells.map(c => {
      const avg = Number(c.avg_months) || 0
      return {
        x: String(c.x_label),
        y: String(c.y_label),
        r: Math.max(3, Math.min(12, Math.sqrt(Number(c.count) || 1) * 2.2)),
        _avg: avg,
        _count: Number(c.count) || 0,
        _early: Number(c.early_count) || 0,
        _on: Number(c.on_time_count) || 0,
        _late: Number(c.late_count) || 0
      }
    })

    const colors = points.map(p => this.colorForAvg(p._avg, maxAbs))

    this.varianceVizChart = new Chart(this.varianceVizCtx, {
      type: 'scatter',
      data: {
        datasets: [{
          label: 'Avg release variance',
          data: points,
          pointRadius: points.map(p => p.r),
          pointHoverRadius: points.map(p => p.r + 2),
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const p = items[0].raw
                return `${p.x} × ${p.y}`
              },
              label: (item) => {
                const p = item.raw
                return `avg months: ${p._avg} (count: ${p._count})`
              },
              afterBody: (items) => {
                const p = items[0].raw
                return `early: ${p._early}, on-time: ${p._on}, late: ${p._late}`
              }
            }
          }
        },
        scales: {
          x: { type: 'category', labels: xLabels, grid: { color: 'rgba(75, 85, 99, 0.2)' }, ticks: { color: '#9CA3AF' } },
          y: {
            type: 'category',
            labels: yLabels,
            // For lead time and reissue buckets, put the smallest at the bottom (origin) instead of the top.
            reverse: ['estimated_release_leadtime', 'reissues_in_production'].includes(this.secondaryAxisValue),
            grid: { color: 'rgba(75, 85, 99, 0.2)' },
            ticks: { color: '#9CA3AF' }
          }
        }
      }
    })
  }

  colorForAvg(avg, maxAbs) {
    const green = [34, 197, 94]
    const blue = [59, 130, 246]
    const red = [239, 68, 68]

    const clamp = (x) => Math.max(0, Math.min(1, x))
    const lerp = (a, b, t) => a + (b - a) * t
    const mix = (c1, c2, t) => {
      const tt = clamp(t)
      const r = Math.round(lerp(c1[0], c2[0], tt))
      const g = Math.round(lerp(c1[1], c2[1], tt))
      const b = Math.round(lerp(c1[2], c2[2], tt))
      return `rgb(${r}, ${g}, ${b})`
    }

    if (avg <= 0) {
      const t = (avg + maxAbs) / maxAbs
      return mix(green, blue, t)
    }
    const t = avg / maxAbs
    return mix(blue, red, t)
  }

  formatDate(dateStr) {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  formatDelta(m) {
    if (m === 0) return 'on time'
    const abs = Math.abs(m)
    return m > 0 ? `late by ${abs} mo` : `early by ${abs} mo`
  }

  debounce(fn, delay) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), delay)
    }
  }

  normalizeParams(params) {
    // Ensure boolean toggles are clean in URL: if checked -> key=1, if not -> remove
    ["include_immediate_release", "exclude_mezco_properties"].forEach((key) => {
      const values = params.getAll(key)
      if (values.includes('1')) {
        params.set(key, '1')
      } else {
        params.delete(key)
      }
    })
    return params
  }

  alignDualAxisZero(chart, avgValues, medianValues) {
    const values = [...avgValues, ...medianValues].filter(v => Number.isFinite(v))
    if (values.length === 0 || !chart) return
    const min = Math.min(...values)
    const max = Math.max(...values)
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return
    // Relative position of zero on left axis (0 to 1)
    const r = (0 - min) / (max - min)
    // Solve for y1.min given y1.max=100 so zero aligns:
    // (0 - min1) / (100 - min1) = r  => min1 = -100*r / (1 - r)
    let min1
    if (r <= 0) {
      min1 = 0
    } else if (r >= 1) {
      min1 = -100
    } else {
      min1 = -100 * r / (1 - r)
    }
    const y1 = chart.options.scales.y1
    y1.min = Math.floor(min1)
    y1.max = 100
  }

  renderCohortTable() {
    if (!this.hasCohortTableBodyTarget) return
    const rows = (this.cohortData || []).map((c) => {
      const label = this.escapeHtml(c.label ?? "")
      const count = Number.isFinite(c.count) ? c.count : 0
      const pct = Number.isFinite(c.percent_late) ? c.percent_late : 0
      const avg = Number.isFinite(c.avg_months) ? c.avg_months : 0
      const med = Number.isFinite(c.median_months) ? c.median_months : 0
      return `
        <tr>
          <td class="px-3 py-2 text-sm text-gray-200">${label}</td>
          <td class="px-3 py-2 text-sm text-right text-gray-200">${count}</td>
          <td class="px-3 py-2 text-sm text-right text-gray-200">${pct}%</td>
          <td class="px-3 py-2 text-sm text-right text-gray-200">${avg}</td>
          <td class="px-3 py-2 text-sm text-right text-gray-200">${med}</td>
        </tr>
      `
    })
    this.cohortTableBodyTarget.innerHTML = rows.join("")
  }

  escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}
